import { useId } from 'react';

import { PlatformFilter, usePlatformDeleteFilterTeam } from '@cloudquery/api-platform';
import { useBaseAuthenticated } from '@cloudquery/base-users';
import CloseIcon from '@mui/icons-material/Close';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { useTableStateContext } from '../../hooks/useTableStateContext';

interface Props {
  filter?: PlatformFilter;
  onClose: () => void;
  onClosed: () => void;
  onDeleted: () => void;
  open: boolean;
}

export function SavedFiltersPanelDeleteDialog({
  filter,
  onClose,
  onClosed,
  onDeleted,
  open,
}: Props) {
  const { currentMembership } = useBaseAuthenticated();
  const { removeSavedFilter } = useTableStateContext();
  const labelId = useId();
  const { isLoading: isDeleting, mutateAsync: platformDeleteFilter } =
    usePlatformDeleteFilterTeam();

  const handleDelete = () => {
    platformDeleteFilter({
      filterId: filter!.id,
      teamName: currentMembership.team.name,
    })
      .then(() => {
        removeSavedFilter();
        onDeleted();
      })
      .catch(() => null);
  };

  return (
    <Dialog
      aria-labelledby={labelId}
      fullWidth={true}
      onClose={onClose}
      open={open}
      slotProps={{
        transition: {
          onExited: onClosed,
        },
      }}
    >
      <DialogTitle id={labelId}>
        <Box alignItems="center" display="flex" justifyContent="space-between">
          <Typography variant="h6">Confirm deletion of the filter</Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Alert
            color="error"
            severity="error"
            sx={{
              width: '100%',
            }}
            variant="filled"
          >
            <Box component="span" fontWeight={600}>
              {filter?.name}
            </Box>{' '}
            filter will be permanently deleted
          </Alert>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button color="secondary" onClick={onClose} size="medium" variant="outlined">
          Cancel
        </Button>
        <Button loading={isDeleting} onClick={handleDelete} size="medium" variant="contained">
          Delete the filter
        </Button>
      </DialogActions>
    </Dialog>
  );
}
